/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DeviceSipProfilesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Device SIP Profiles module.
----------------------------------------------------------------*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Copy, Smartphone } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import Modal from '../../components/ui/Modal';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { useDeviceSipProfileList } from './hooks';
import { deleteDeviceSipProfile } from './api';
import { DeviceSipProfile } from './types';
import {
  navigateToDeviceSipProfileDuplicate,
} from './deviceSipProfilesActions';

export default function DeviceSipProfilesPage() {
  const { t } = useTranslation(['deviceSipProfiles', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<DeviceSipProfile | null>(null);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('deviceSipProfiles:name') || 'Name', visible: true, sortable: true },
    { key: 'description', label: t('common:description') || 'Description', visible: true, sortable: true },
    { key: 'isDefault', label: t('common:default') || 'Default', visible: true, sortable: true },
    { key: 'enabled', label: t('common:status') || 'Status', visible: true, sortable: true },
    { key: '_count', label: t('deviceSipProfiles:usage') || 'Usage', visible: true, sortable: false },
    { key: 'actions', label: t('common:actions') || 'Actions', visible: true, sortable: false },
  ], [t]);

  // Use custom hook for list management
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
  } = useDeviceSipProfileList({ initialColumns: availableColumns });

  const handleEdit = useCallback((profile: DeviceSipProfile) => {
    navigate(`/settings/technology/device-profile/${profile.uuid ?? profile.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((profile: DeviceSipProfile) => {
    navigateToDeviceSipProfileDuplicate(navigate, profile.uuid ?? profile.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((profile: DeviceSipProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!profileToDelete) return;

    setIsDeletingProfile(true);
    try {
      await deleteDeviceSipProfile(profileToDelete.id);

      showSuccess(t('deviceSipProfiles:deleteSuccess'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      // Refresh the list by triggering a page reload or refetch
      window.location.reload();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      showError(error.message || t('deviceSipProfiles:deleteError'));
    } finally {
      setIsDeletingProfile(false);
    }
  }, [profileToDelete, t, showSuccess, showError]);

  const renderActions = useCallback((profile: DeviceSipProfile) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common:edit')}>
        <button
          type="button"
          onClick={() => handleEdit(profile)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common:duplicate')}>
        <button
          type="button"
          onClick={() => handleDuplicate(profile)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip
        content={
          profile._count.devices > 0
            ? t('deviceSipProfiles:cannotDeleteInUse', { count: profile._count.devices })
            : t('common:delete')
        }
      >
        <button
          type="button"
          onClick={() => handleDeleteClick(profile)}
          className={`p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 ${profile._count.devices > 0
            ? 'text-gray-400 cursor-not-allowed'
            : 'hover:bg-red-100 dark:hover:bg-red-900/30'
            }`}
          disabled={profile._count.devices > 0}
        >
          <Trash2 className={`h-4 w-4 ${profile._count.devices > 0 ? '' : 'text-red-600 dark:text-red-400'}`} />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  const enhancedColumns = useMemo(() => (columns || []).map((col) => {
    if (col.key === 'isDefault') {
      return {
        ...col,
        render: (value: boolean) => (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${value
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}
          >
            {value ? t('common:yes') : t('common:no')}
          </span>
        ),
      };
    }

    if (col.key === 'enabled') {
      return {
        ...col,
        render: (value: boolean) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${value
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
              }`}
          >
            {value ? t('common:active') : t('common:inactive')}
          </span>
        ),
      };
    }

    if (col.key === '_count') {
      return {
        ...col,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_: any, profile: DeviceSipProfile) => (
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground">
              {profile._count.devices} {t('deviceSipProfiles:devices')}
            </span>
            {profile._count.devices > 0 ? (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded text-xs font-medium">
                {t('common:inUse')}
              </span>
            ) : (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-xs font-medium">
                {t('common:notInUse')}
              </span>
            )}
          </div>
        ),
      };
    }

    if (col.key === 'actions') {
      return {
        ...col,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_: any, profile: DeviceSipProfile) => renderActions(profile),
      };
    }

    return col;
  }), [columns, renderActions, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            title={t('deviceSipProfiles:title') || 'Device SIP Profiles'}
            titleIcon={<Smartphone className="h-5 w-5 text-primary" />}
            data={data}
            columns={enhancedColumns}
            pagination={pagination}
            onAdd={() => navigate('/settings/technology/device-profile/new')}
            addButtonText={t('common:add') || 'Add'}
            loading={loading}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            emptyMessage={t('common:noItemsFound')}
            searchPlaceholder={t('common:search')}
            translations={{
              items: t('common:items'),
              columns: t('common:columns'),
              noItemsFound: t('common:noItemsFound'),
              showing: t('common:showing'),
              to: t('common:to'),
              of: t('common:of'),
              results: t('common:results'),
              previous: t('common:previous'),
              next: t('common:next'),
              page: t('common:page'),
              deleteItem: t('common:deleteItem'),
              areYouSureDeleteItem: t('common:areYouSureDeleteItem'),
              delete: t('common:delete'),
              cancel: t('common:cancel'),
              allSelected: t('common:allSelected'),
              selected: t('common:selected'),
              searchOptions: t('common:searchOptions'),
              noOptionsFound: t('common:noOptionsFound'),
              selectAll: t('common:selectAll'),
              clearAll: t('common:clearAll'),
            }}
          />

          {/* Delete Confirmation Modal */}
          {showDeleteModal && profileToDelete && (
            <Modal
              isOpen={showDeleteModal}
              onClose={() => {
                setShowDeleteModal(false);
                setProfileToDelete(null);
              }}
              title={t('deviceSipProfiles:deleteConfirmTitle')}
            >
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t('deviceSipProfiles:deleteConfirmMessage', { name: profileToDelete.name })}
                </p>

                {profileToDelete._count.devices > 0 && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      {t('deviceSipProfiles:deleteWarning', {
                        count: profileToDelete._count.devices,
                      })}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setProfileToDelete(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    disabled={isDeletingProfile}
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    disabled={isDeletingProfile || profileToDelete._count.devices > 0}
                  >
                    {isDeletingProfile ? t('common:deleting') : t('common:delete')}
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </>
      }
      formView={null}
    />
  );
}

